import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { format } from "date-fns";
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    CircularProgress,
    FormControl, 
    MenuItem, 
    InputLabel, 
    Select,      
} from '@mui/material';
import useAuthCheck from 'hooks/useAuthCheck';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { LAYOUT_HEIGHT, DIVERT_RETURN_STATUS, EXCLUDED_FLIGHT_STATUS } from 'state/constants';
import { removeItemByValueImmutable, filterFlights } from 'state/helpers';
import { countValuesByField } from './utils';
import Header from 'components/Header';
import FilterButton from 'components/FilterButton';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import FlightFilterDialog from 'components/FlightFilterDialog';
import ChartDialog from 'components/chartNivo/ChartDialog';
import SortBarMultiLinesChart from 'components/chartNivo/SortBarMultiLinesChart';
import { boxStyles as styles } from 'components/CustomStyles';


const viewOptions = [
  { value: "btDiffByFlightDate", label: "by Date" },
  { value: "btDiffByDep", label: "by Port" },
  { value: "btDiffBySector", label: "by Sector" },
  { value: "btDiffBySectorType", label: "by Sector Type" },
  { value: "btDiffByFlightType", label: "by Flight Type" },
  { value: "btDiffByFlightReg", label: "by Flight Reg" },
  { value: "btDiffByACType", label: "by AC Type" },
  { value: "btDiffByCountry", label: "by Country" },
  { value: "btDiffByFlightNo", label: "by Flight No" },
];

const BTDiffChart = () => {   
    useAuthCheck();
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { startDate, endDate } = useDateRangeContext();

    const scrollRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState(null);
    const [chartOpen, setChartOpen] = useState(false);
    
    const [view, setView] = useState('btDiffByFlightDate');
    const [flights, setFlights] = useState([]); 
    const flightsData = useSelector((state) => state.flights); 

    useEffect(() => {        
        const handleFlights = async () => {
            try {           
                setLoading(true); 
                const processedFlights = removeItemByValueImmutable(flightsData, 'flightStatus', EXCLUDED_FLIGHT_STATUS)
                    .filter(flight => 
                        !DIVERT_RETURN_STATUS.includes(flight.flightStatus)
                    );
                setFlights(processedFlights);                
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };        
        handleFlights();      
    }, [flightsData]);
        
    const filteredFlights = useMemo(() => {
        return filterFlights({ flights, filters, timeMode });
    }, [flights, filters, timeMode]);      

    const computeChartData = useCallback((view, flights, timeMode) => {
        const flightDate = timeMode === "UTC" ? "flightDate" : "flightDateLocalBase";

        switch (view) {
            case "btDiffByFlightDate":
                return countValuesByField(flights, "btDiff", flightDate);

            case "btDiffByDep":
                return countValuesByField(flights, "btDiff", "dep");

            case "btDiffBySector":
                return countValuesByField(flights, "btDiff", "sector");

            case "btDiffBySectorType":
                return countValuesByField(flights, "btDiff", "sectorType");

            case "btDiffByFlightType":
                return countValuesByField(flights, "btDiff", "flightType");

            case "btDiffByFlightReg":
                return countValuesByField(flights, "btDiff", "flightReg");

            case "btDiffByACType":
                return countValuesByField(flights, "btDiff", "acType");

            case "btDiffByCountry":
                return countValuesByField(flights, "btDiff", "depCountryName");

            case "btDiffByFlightNo":
                return countValuesByField(flights, "btDiff", "flightNo");

            default:
                return [];
        }
    }, []);
    
    const chartData = useMemo(() => {
        return computeChartData(view, filteredFlights, timeMode);
    }, [view, filteredFlights, timeMode, computeChartData]);
    
    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
    
    const handleFilterApply = (filterValues) => {
        setFilters(filterValues); 
    };
        
    const {
        acTypeOptions,
        flightRegOptions,
        sectorTypeOptions,
        departureTypeOptions,
        arrivalTypeOptions,
    } = useMemo(() => {
        if (!Array.isArray(flights)) {
            return {
                acTypeOptions: [],
                flightRegOptions: [],
                sectorTypeOptions: [],
                departureTypeOptions: [],
                arrivalTypeOptions: [],
            };
        }

        const sorted = (key) =>
            [...new Set(flights.map((f) => f[key]).filter(Boolean))].sort();

        return {
            acTypeOptions: sorted("acType"),
            flightRegOptions: sorted("flightReg"),
            sectorTypeOptions: sorted("sectorType"),
            departureTypeOptions: sorted("departureType"),
            arrivalTypeOptions: sorted("arrivalType"),
        };
    }, [flights]);

    const handleOpen = () => {
        setChartOpen(true);
    };

    const handleClose = () => {
        setChartOpen(false);
    };

    const chartMap = useMemo(() => {
        const period = `${startDate ? format(startDate, 'ddMMMyy') : ''} - ${endDate ? format(endDate, 'ddMMMyy') : ''}`;
        const defaultTitle = `BT Diff ${period}`;

        return ({
            btDiffByFlightDate: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "Date", leftLegend: "Minutes", digit: 1, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 } },
            btDiffByDep: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "Port", leftLegend: "Minutes", digit: 1, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 } },
            btDiffBySector: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "Sector", leftLegend: "Minutes", digit: 1, bottomOffSet: 10, bottomLegendOffSet: 10, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 } },
            btDiffBySectorType: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "Sector Type", leftLegend: "Minutes", isHorizontalBottomLegend: true, digit: 1, bottomOffSet: -30, bottomLegendOffSet: -30, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 } },
            btDiffByFlightType: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "Flight Type", leftLegend: "Minutes", isHorizontalBottomLegend: !isMobile ? true : false, digit: 1, bottomOffSet: !isMobile ? -30 : 50, bottomLegendOffSet: !isMobile ? -30 : 50, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 } },
            btDiffByFlightReg: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "Flight Reg", leftLegend: "Minutes", digit: 1, bottomOffSet: 5, bottomLegendOffSet: 5, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 } },
            btDiffByACType: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "AC Type", leftLegend: "Minutes", digit: 1, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 } },
            btDiffByCountry: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "Country", leftLegend: "Minutes", digit: 0, bottomOffSet: isMobile ? 55 : 45, bottomLegendOffSet: isMobile ? 45 : 45, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 } },
            btDiffByFlightNo: { component: SortBarMultiLinesChart, props: { title: defaultTitle, bottomLegend: "Flight No", leftLegend: "Minutes", digit: 1, bottomOffSet: 5, bottomLegendOffSet: 5, barTranslateXOffSet: 80, lineTranslateXOffSet: -10 } },
        })
    }, [startDate, endDate, isMobile]);
    
    const renderChart = useCallback((view, customStyles) => {
        const chart = chartMap[view];

        if (!chart) {
            return (
                <Box sx={{ ...styles, ...customStyles }}>
                    <Typography>No charts available</Typography>
                </Box>
            );
        }

        const Component = chart.component;
        return (
            <Box sx={{ ...styles, ...customStyles }}>
                <Component data={chartData} {...chart.props} />
            </Box>
        );
    }, [chartMap, chartData]);


    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header 
                    title="Actual - Schedule BT Diff" 
                    subtitle={`${loading ? 'Loading...' : 'List of Charts'}`}
                />     
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <FormControl
                            size="small"
                            sx={{ width: { xs: '8rem', sm: '9rem' } }}
                        >
                            <InputLabel id="bt-diff-view-label">View</InputLabel>
                            <Select
                                labelId="bt-diff-view-label"
                                label="View"
                                value={view}
                                onChange={(e) => setView(e.target.value)}
                            >
                                {viewOptions.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FilterButton
                            filters={filters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )}                    
            </FlexBetween>               
            {loading ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <Typography color="error">
                        Error: {error?.message || ''}
                    </Typography>
                </Box>
            ) : (
                <Box
                    display="grid"                        
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                     
                >
                    <Box
                        ref={scrollRef}
                        height={LAYOUT_HEIGHT}  
                        display="flex"
                        flexDirection="column"
                        gap={2}
                        sx={{ gridColumn: "span 12", overflowY: 'auto' }}
                        onDoubleClick={handleOpen}
                    >
                        {renderChart(view, { minHeight: LAYOUT_HEIGHT })}    

                        <ScrollToTop scrollRef={scrollRef} />                     
                    </Box>
                </Box>
            )}
            
            {isFilterDialogOpen &&
                <FlightFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    acTypeOptions={acTypeOptions} 
                    flightRegOptions={flightRegOptions}
                    sectorTypeOptions={sectorTypeOptions}
                    departureTypeOptions={departureTypeOptions}
                    arrivalTypeOptions={arrivalTypeOptions}
                    currentFilters={filters}
                />
            }

            {chartOpen && (
                <ChartDialog
                    open={chartOpen}
                    onClose={handleClose}
                    chartComponent={renderChart(view, {})}
                />
            )}
        </Box>
    );
};

export default BTDiffChart;
